import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Bell, Mail, ShieldAlert, Megaphone, Tag, Calendar,
  Search, Info, Check, X, Loader2, ShieldCheck, Pencil, Send,
} from 'lucide-react';
import { API, useAuth } from '../App';
import { getDeviceSubscriptionId } from '../lib/deviceSubscription';

/**
 * Iter 210 — Notification Preferences (anonymous + registered).
 *
 * Single page where the user controls:
 *   - Push notifications master toggle
 *   - Email notifications master toggle (requires verified email)
 *   - 6 per-category opt-ins
 *   - Critical Email Backup override (sends critical safety alerts by
 *     email even when email is otherwise off)
 *
 * Also surfaces the "Add backup email" workflow for anonymous users so
 * they can recover their account after a phone change.
 *
 * If the user is signed in, talks to `/api/user/notification-preferences`.
 * If anonymous, talks to `/api/me/preferences` and `/api/me/email/*`.
 */

const CATEGORY_META = [
  { key: 'criticalSafety', label: 'Critical Safety Alerts', desc: 'Floods, fires, road closures, utility outages, police notices.', icon: ShieldAlert, color: 'text-red-600' },
  { key: 'community', label: 'Community Alerts', desc: 'Neighbour safety alerts, missing pets, weather warnings.', icon: Megaphone, color: 'text-amber-600' },
  { key: 'businessOffers', label: 'Business Offers', desc: 'Discounts and promotions from businesses you follow.', icon: Tag, color: 'text-emerald-600' },
  { key: 'events', label: 'Events', desc: 'New events from businesses you follow and your saved towns.', icon: Calendar, color: 'text-violet-600' },
  { key: 'lostAndFound', label: 'Lost & Found', desc: 'Lost or found items posted in your saved towns.', icon: Search, color: 'text-cyan-700' },
  { key: 'systemUpdates', label: 'System Updates', desc: 'Account, maintenance, product news from Find-2-Day.', icon: Info, color: 'text-slate-600' },
];

const NotificationPreferences = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAuthed = !!user?.token;
  const subscriptionId = isAuthed ? '' : getDeviceSubscriptionId();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [pendingVerify, setPendingVerify] = useState(false);

  // ---------- Fetch ----------
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (isAuthed) {
          const r = await axios.get(`${API}/user/notification-preferences`, {
            headers: { Authorization: `Bearer ${user.token}` },
          });
          if (alive) {
            setPrefs(r.data);
            setRecoveryEmail((user.email || '').toLowerCase());
            setEmailVerified(true); // registered users have a verified email
          }
        } else {
          const r = await axios.get(`${API}/me/preferences`, { params: { subscriptionId } });
          if (alive) {
            setPrefs(r.data.preferences);
            setRecoveryEmail(r.data.recoveryEmail || '');
            setEmailVerified(!!r.data.recoveryEmailVerified);
            setPendingVerify(!!r.data.recoveryEmail && !r.data.recoveryEmailVerified);
          }
        }
      } catch (err) {
        toast.error('Could not load your preferences');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [isAuthed, subscriptionId, user]);

  // ---------- Save preferences ----------
  const saveDebounced = async (next) => {
    setSaving(true);
    try {
      if (isAuthed) {
        const r = await axios.put(`${API}/user/notification-preferences`, next, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        setPrefs(r.data);
      } else {
        const r = await axios.put(`${API}/me/preferences`, {
          subscriptionId, preferences: next,
        });
        setPrefs(r.data.preferences);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const togglePush = () => {
    const next = { ...prefs, pushEnabled: !prefs.pushEnabled };
    setPrefs(next);
    saveDebounced({ pushEnabled: next.pushEnabled });
  };

  const toggleEmail = () => {
    if (!emailVerified && !prefs.emailEnabled) {
      toast.error('Add and verify a backup email first');
      return;
    }
    const enabling = !prefs.emailEnabled;
    // Turning the master email toggle ON also opts the user into the content
    // categories that map to "updates from the businesses / towns you follow"
    // (Events, Offers, Lost & Found, Community, Critical Safety). The send-time
    // gate requires emailEnabled AND the per-category flag, so without this the
    // toggle alone would deliver nothing for Events/Offers. System/marketing
    // updates stay OFF, and the user can still opt out of any category below.
    const patch = enabling
      ? {
          emailEnabled: true,
          categories: {
            events: true, businessOffers: true, lostAndFound: true,
            community: true, criticalSafety: true,
          },
        }
      : { emailEnabled: false };
    setPrefs((p) => ({
      ...p,
      emailEnabled: enabling,
      categories: enabling ? { ...p.categories, ...patch.categories } : p.categories,
    }));
    saveDebounced(patch);
  };

  const toggleCategory = (key) => {
    const next = { ...prefs, categories: { ...prefs.categories, [key]: !prefs.categories[key] } };
    setPrefs(next);
    saveDebounced({ categories: { [key]: next.categories[key] } });
  };

  const toggleCriticalBackup = () => {
    if (!emailVerified && !prefs.criticalEmailBackup) {
      toast.error('Add and verify a backup email first');
      return;
    }
    const next = { ...prefs, criticalEmailBackup: !prefs.criticalEmailBackup };
    setPrefs(next);
    saveDebounced({ criticalEmailBackup: next.criticalEmailBackup });
  };

  // ---------- Email attach / verify / remove ----------
  const attachEmail = async () => {
    const value = newEmail.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(value)) {
      toast.error('Please enter a valid email address');
      return;
    }
    try {
      await axios.post(`${API}/me/email/add`, { subscriptionId, email: value });
      setRecoveryEmail(value);
      setEditingEmail(false);
      setPendingVerify(true);
      toast.success('Verification email sent — check your inbox');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save email');
    }
  };

  const verifyByCode = async () => {
    if (!verifyCode.trim()) return;
    try {
      await axios.post(`${API}/me/email/verify`, {
        email: recoveryEmail, code: verifyCode.trim(),
      });
      setEmailVerified(true);
      setPendingVerify(false);
      setVerifyCode('');
      toast.success('Email verified — your account is now backed up');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid or expired code');
    }
  };

  const resendVerification = async () => {
    try {
      await axios.post(`${API}/me/email/resend-verify`, { subscriptionId });
      toast.success('Verification email re-sent');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not resend');
    }
  };

  const removeEmail = async () => {
    if (!window.confirm('Remove backup email? You will no longer be able to recover your account from another device.')) return;
    try {
      await axios.request({ url: `${API}/me/email`, method: 'DELETE', data: { subscriptionId } });
      setRecoveryEmail('');
      setEmailVerified(false);
      setPendingVerify(false);
      // Email channel must also turn off when email is removed.
      const next = { emailEnabled: false, criticalEmailBackup: false };
      setPrefs((p) => ({ ...p, ...next }));
      await saveDebounced(next);
      toast.success('Backup email removed');
    } catch (err) {
      toast.error('Could not remove email');
    }
  };

  if (loading || !prefs) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="notification-preferences-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1 rounded hover:bg-slate-100" data-testid="prefs-back">
            <ArrowLeft className="h-5 w-5 text-slate-700" />
          </button>
          <div className="flex-1">
            <h1 className="text-base font-bold text-slate-900">Notifications</h1>
            <p className="text-[11px] text-slate-500">Choose what and how you hear from Find-2-Day</p>
          </div>
          {saving && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-5 space-y-5">
        {/* Anonymous notice */}
        {!isAuthed && (
          <div className="bg-violet-50 border border-violet-200 rounded-xl p-3 text-[12px] text-violet-900">
            <strong>Tip:</strong> add an optional backup email below to recover your follows,
            favourites and settings if you change phone or reinstall the app.
          </div>
        )}

        {/* Channel toggles */}
        <section className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-200" data-testid="channel-toggles">
          <ChannelRow
            icon={<Bell className="h-5 w-5 text-violet-600" />}
            title="Push notifications"
            subtitle="Real-time alerts on this device"
            enabled={prefs.pushEnabled}
            onToggle={togglePush}
            testId="toggle-push"
          />
          <ChannelRow
            icon={<Mail className="h-5 w-5 text-emerald-600" />}
            title="Email notifications"
            subtitle={emailVerified ? `Sent to ${recoveryEmail}` : 'Add and verify a backup email to enable'}
            enabled={prefs.emailEnabled}
            onToggle={toggleEmail}
            disabled={!emailVerified}
            testId="toggle-email"
          />
        </section>

        {/* Backup email management (for anonymous users) */}
        {!isAuthed && (
          <section className="bg-white border border-slate-200 rounded-xl p-4" data-testid="backup-email-section">
            <h2 className="text-sm font-bold text-slate-900 mb-1">Backup email</h2>
            <p className="text-[12px] text-slate-500 mb-3">Optional. Lets you restore your account on a new phone and turn on email alerts.</p>

            {!recoveryEmail && !editingEmail && (
              <button
                onClick={() => setEditingEmail(true)}
                className="w-full px-4 py-2.5 bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold rounded-lg flex items-center justify-center gap-2"
                data-testid="add-backup-email-btn"
              >
                <Mail className="h-4 w-4" /> Add backup email
              </button>
            )}

            {editingEmail && (
              <div className="space-y-2" data-testid="add-email-form">
                <input
                  type="email" autoFocus
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  data-testid="new-email-input"
                />
                <div className="flex gap-2">
                  <button onClick={attachEmail} className="flex-1 px-3 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold rounded-lg" data-testid="save-email-btn">
                    Send verification
                  </button>
                  <button onClick={() => { setEditingEmail(false); setNewEmail(''); }} className="px-3 py-2 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {recoveryEmail && !editingEmail && (
              <div data-testid="current-email-block">
                <div className="flex items-center gap-2 mb-3">
                  <Mail className="h-4 w-4 text-slate-500" />
                  <span className="text-sm text-slate-900 font-medium flex-1 break-all">{recoveryEmail}</span>
                  {emailVerified ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-bold rounded-full">
                      <ShieldCheck className="h-3 w-3" /> Verified
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-bold rounded-full">
                      Pending
                    </span>
                  )}
                </div>

                {pendingVerify && !emailVerified && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 space-y-2" data-testid="verify-block">
                    <p className="text-[12px] text-amber-900">
                      We sent a 6-digit code and a magic link to <strong className="break-all">{recoveryEmail}</strong>.
                      Click the link, or paste the code below.
                    </p>
                    <div className="flex gap-2">
                      <input
                        value={verifyCode}
                        onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="6-digit code"
                        inputMode="numeric"
                        className="flex-1 px-3 py-2 border border-amber-300 rounded-lg text-center text-lg font-bold tracking-widest"
                        data-testid="verify-code-input"
                      />
                      <button onClick={verifyByCode} className="px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-lg" data-testid="verify-code-btn">
                        Verify
                      </button>
                    </div>
                    <button onClick={resendVerification} className="text-[12px] text-violet-700 hover:underline inline-flex items-center gap-1" data-testid="resend-btn">
                      <Send className="h-3 w-3" /> Resend email
                    </button>
                  </div>
                )}

                <div className="flex gap-2">
                  <button onClick={() => { setEditingEmail(true); setNewEmail(recoveryEmail); }} className="text-[12px] text-violet-700 hover:underline inline-flex items-center gap-1" data-testid="change-email-btn">
                    <Pencil className="h-3 w-3" /> Change
                  </button>
                  <span className="text-[12px] text-slate-300">·</span>
                  <button onClick={removeEmail} className="text-[12px] text-red-600 hover:underline" data-testid="remove-email-btn">
                    Remove
                  </button>
                </div>
              </div>
            )}
          </section>
        )}

        {/* Critical Email Backup override */}
        <section className="bg-white border border-slate-200 rounded-xl p-4" data-testid="critical-backup-section">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-red-50 rounded-lg"><ShieldAlert className="h-5 w-5 text-red-600" /></div>
            <div className="flex-1">
              <h2 className="text-sm font-bold text-slate-900">Email backup for Official Community Safety Alerts</h2>
              <p className="text-[12px] text-slate-500 mt-0.5">
                When ON, floods, fires, road closures, utility outages, police notices and
                official town-hall alerts will also be sent to your registered email — even when
                you have marketing/other emails switched off.{' '}
                <strong>This covers safety communications only, not marketing.</strong>
              </p>
              <p className="text-[11px] text-slate-500 mt-1">
                This may be enabled by default on your account because Community Safety Alerts
                are considered an essential service. You can turn it OFF here at any time; your
                marketing and non-safety email preferences are always separate.
              </p>
            </div>
            <Toggle enabled={prefs.criticalEmailBackup} onToggle={toggleCriticalBackup} disabled={!emailVerified} testId="toggle-critical-backup" />
          </div>
        </section>

        {/* Per-category */}
        <section className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-200" data-testid="categories-section">
          <div className="px-4 py-3 bg-slate-50 rounded-t-xl">
            <h2 className="text-sm font-bold text-slate-900">Categories</h2>
            <p className="text-[11px] text-slate-500">Apply to both push and email when each channel is on.</p>
          </div>
          {CATEGORY_META.map(({ key, label, desc, icon: Icon, color }) => (
            <ChannelRow
              key={key}
              icon={<Icon className={`h-5 w-5 ${color}`} />}
              title={label}
              subtitle={desc}
              enabled={prefs.categories?.[key]}
              onToggle={() => toggleCategory(key)}
              testId={`toggle-cat-${key}`}
            />
          ))}
        </section>

        <p className="text-center text-[11px] text-slate-400">
          Want to recover your account on this device?{' '}
          <Link to="/restore" className="text-violet-700 hover:underline">Restore from backup</Link>
        </p>
      </main>
    </div>
  );
};

const Toggle = ({ enabled, onToggle, disabled, testId }) => (
  <button
    type="button"
    role="switch"
    aria-checked={!!enabled}
    onClick={disabled ? undefined : onToggle}
    disabled={disabled}
    data-testid={testId}
    className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
      disabled ? 'bg-slate-200 cursor-not-allowed' : enabled ? 'bg-violet-600' : 'bg-slate-300'
    }`}
  >
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0'}`} />
  </button>
);

const ChannelRow = ({ icon, title, subtitle, enabled, onToggle, disabled, testId }) => (
  <div className="flex items-start gap-3 p-4">
    <div className="p-2 bg-slate-100 rounded-lg flex-shrink-0">{icon}</div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-slate-900">{title}</p>
      <p className="text-[11px] text-slate-500 leading-relaxed">{subtitle}</p>
    </div>
    <Toggle enabled={enabled} onToggle={onToggle} disabled={disabled} testId={testId} />
  </div>
);

export default NotificationPreferences;
